import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Trophy, BookOpen } from "lucide-react";

const PERIODS = [{ k: "weekly", l: "Haftalık" }, { k: "monthly", l: "Aylık" }, { k: "all", l: "Tüm Zamanlar" }];
const BOOK_METRICS = [
  { k: "okunan", l: "En Çok Okunan", suffix: "okuma" },
  { k: "yorum", l: "En Çok Yorumlanan", suffix: "yorum" },
  { k: "alinti", l: "En Çok Alıntılanan", suffix: "alıntı" },
  { k: "puan", l: "En Yüksek Puan", suffix: "★" },
];

export default function Leaderboards() {
  const [period, setPeriod] = useState("weekly");
  const [data, setData] = useState({ liste: [], benim: null });
  const [bookMetric, setBookMetric] = useState("okunan");
  const [books, setBooks] = useState([]);

  useEffect(() => { api.get("/leaderboard", { params: { period } }).then((r) => setData(r.data)); }, [period]);
  useEffect(() => { api.get("/leaderboard/books", { params: { metric: bookMetric, period } }).then((r) => setBooks(r.data)); }, [bookMetric, period]);

  const top3 = data.liste.slice(0, 3);
  const rest = data.liste.slice(3);
  const order = [1, 0, 2];
  const medal = ["🥇", "🥈", "🥉"];
  const suffix = BOOK_METRICS.find((m) => m.k === bookMetric)?.suffix || "";

  return (
    <div className="max-w-4xl mx-auto pb-28">
      <div className="fade-up">
        <p className="text-xs uppercase tracking-[0.2em] font-bold text-terra flex items-center gap-2"><Trophy size={14} /> Liderlik Tablosu</p>
        <h1 className="font-serif text-4xl sm:text-5xl text-ink tracking-tight mt-1">Topluluğun Bilgeleri</h1>
      </div>

      <div className="flex gap-2 mt-6">
        {PERIODS.map((p) => (
          <button key={p.k} onClick={() => setPeriod(p.k)} data-testid={`period-${p.k}`}
            className={`text-sm px-4 py-1.5 rounded-full border ${period === p.k ? "bg-terra text-white border-terra" : "border-line text-ink-2"}`}>{p.l}</button>
        ))}
      </div>

      {top3.length > 0 && (
        <div className="grid grid-cols-3 gap-2 sm:gap-4 mt-8 items-end">
          {order.map((idx) => top3[idx] && (
            <Link to={`/uye/${top3[idx].id}`} key={idx}
              className={`border border-line rounded-xl bg-surface p-3 text-center ${idx === 0 ? "sm:pb-8 border-gold/40 bg-gold/5" : ""}`}>
              <div className="text-3xl">{medal[idx]}</div>
              <div className="w-14 h-14 mx-auto rounded-full bg-terra/10 border border-line flex items-center justify-center overflow-hidden my-2">
                {top3[idx].profil_resmi ? <img src={top3[idx].profil_resmi} alt="" className="w-full h-full object-cover" /> : <span className="font-serif text-2xl text-terra">{top3[idx].ad_soyad?.[0]}</span>}
              </div>
              <p className="font-medium text-ink text-sm truncate">{top3[idx].ad_soyad}</p>
              <p className="text-[11px] text-muted-foreground">{top3[idx].level_name}</p>
              <p className="text-terra font-bold mt-1">{top3[idx].ap} AP</p>
            </Link>
          ))}
        </div>
      )}

      <div className="mt-6 border border-line rounded-xl bg-surface divide-y divide-line/60">
        {rest.map((r) => (
          <Link to={`/uye/${r.id}`} key={r.id} className="flex items-center gap-3 p-3 hover:bg-surface-alt" data-testid={`rank-${r.sira}`}>
            <span className="font-serif text-xl w-7 text-center text-muted-foreground">{r.sira}</span>
            <div className="w-9 h-9 rounded-full bg-terra/10 border border-line flex items-center justify-center overflow-hidden shrink-0">
              {r.profil_resmi ? <img src={r.profil_resmi} alt="" className="w-full h-full object-cover" /> : <span className="font-serif text-terra">{r.ad_soyad?.[0]}</span>}
            </div>
            <div className="flex-1 min-w-0"><p className="text-sm font-medium text-ink truncate">{r.ad_soyad}</p><p className="text-[11px] text-muted-foreground">{r.level_name} · 🏆 {r.rozet} · 📖 {r.okunan}</p></div>
            <span className="text-sm font-medium text-terra">{r.ap} AP</span>
          </Link>
        ))}
        {data.liste.length === 0 && <p className="p-6 text-center text-sm text-muted-foreground">Bu dönemde henüz puan yok.</p>}
      </div>

      {/* Kitap istatistikleri */}
      <div className="mt-12 fade-up">
        <h2 className="font-serif text-3xl text-ink flex items-center gap-2"><BookOpen size={22} className="text-terra" /> Kitap İstatistikleri</h2>
        <p className="text-sm text-muted-foreground mt-1">{PERIODS.find((p) => p.k === period)?.l} dönemine göre topluluğun en çok ilgilendiği kitaplar.</p>
        <div className="flex gap-2 flex-wrap mt-4">
          {BOOK_METRICS.map((m) => (
            <button key={m.k} onClick={() => setBookMetric(m.k)} data-testid={`book-metric-${m.k}`}
              className={`text-sm px-4 py-1.5 rounded-full border ${bookMetric === m.k ? "bg-sage text-white border-sage" : "border-line text-ink-2"}`}>{m.l}</button>
          ))}
        </div>
        <div className="mt-4 border border-line rounded-xl bg-surface divide-y divide-line/60" data-testid="book-leaderboard">
          {books.length === 0 && <p className="p-6 text-center text-sm text-muted-foreground">Bu dönemde veri yok.</p>}
          {books.map((b, i) => (
            <Link to={`/kitap/${b.id}`} key={b.id} className="flex items-center gap-3 p-3 hover:bg-surface-alt">
              <span className={`font-serif text-xl w-7 text-center ${i < 3 ? "text-gold" : "text-muted-foreground"}`}>{i + 1}</span>
              <div className="flex-1 min-w-0"><p className="text-sm font-medium text-ink truncate">{b.baslik}</p><p className="text-[11px] text-muted-foreground truncate">{b.yazar}</p></div>
              <span className="text-sm font-medium text-terra shrink-0">{b.deger} {suffix}</span>
            </Link>
          ))}
        </div>
      </div>

      {data.benim && (
        <div className="fixed bottom-16 md:bottom-4 left-4 right-4 max-w-4xl mx-auto z-30" data-testid="my-rank-bar">
          <div className="bg-ink text-page rounded-full px-5 py-3 shadow-xl flex items-center gap-3">
            <span className="font-serif text-lg text-gold">#{data.benim.sira}</span>
            <span className="text-sm flex-1">Sen — {data.benim.level_name}</span>
            <span className="text-sm font-medium text-gold">{data.benim.ap} AP</span>
          </div>
        </div>
      )}
    </div>
  );
}
